/**
 * Member wallet settings: 堂票種類 (this venue), 票券種類 and the 月友
 * setting (shared by every venue). Lists need member:read; changes need member:wallet_manage.
 * Hidden outside association mode (the API answers 409 ASSOCIATION_MODE_REQUIRED).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import type {
  MemberLevelDto,
  MonthlyPlanSettingDto,
  PackageCoverage,
  PackageTypeDto,
  VoucherPurpose,
  VoucherTypeDto,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { errorMessage } from './ledger/errorMessage';
import {
  BUTTON,
  Field,
  FormError,
  INPUT,
  PANEL,
  SMALL_BUTTON,
  useWalletPermissions,
} from './ledger/walletShared';

const associationOnly = (error: unknown) => isAxiosError(error) && error.response?.status === 409;

export function WalletCatalogSections({
  activeSection = 'passes',
}: {
  activeSection?: 'passes' | 'vouchers' | 'monthly';
}) {
  const { t } = useTranslation('member');
  const activeTab = ['passes', 'vouchers', 'monthly'].indexOf(activeSection);
  const tabs = ['walletPassTab', 'walletVoucherTab', 'walletPlanTitle'];
  const { data, error, isLoading } = useQuery<PackageTypeDto[]>({
    queryKey: ['package-types'],
    queryFn: () => api.get('/members/package-types'),
    retry: (count, failure) => !associationOnly(failure) && count < 2,
  });
  if (isLoading || associationOnly(error)) return null;
  return (
    <div className="space-y-6 mt-8">
      {/* Keep forms mounted across sidebar destinations to retain edits and pending locks. */}
      {[
        <PackageTypeSection types={data ?? []} />,
        <VoucherTypeSection />,
        <MonthlyPlanSection />,
      ].map((panel, index) => (
        <div
          key={tabs[index]}
          id={`wallet-panel-${index}`}
          role="region"
          aria-label={t(tabs[index]!)}
          hidden={activeTab !== index}
          tabIndex={0}
        >
          {panel}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ title, shared }: { title: string; shared?: boolean }) {
  const { t } = useTranslation('member');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="text-lg font-bold">{title}</h2>
      <span className="text-xs px-2 py-0.5 rounded-full bg-base-300 text-base-content/70">
        {shared ? t('walletSharedAllVenues') : t('walletThisVenue')}
      </span>
    </div>
  );
}

interface PackageForm {
  name: string;
  useCount: string;
  price: string;
  validDays: string;
  covers: PackageCoverage;
  buyInAmounts: string;
  allowRebuyAddon: boolean;
}
const EMPTY_PACKAGE: PackageForm = {
  name: '',
  useCount: '10',
  price: '',
  validDays: '',
  covers: 'BOTH',
  buyInAmounts: '',
  allowRebuyAddon: true,
};

function PackageTypeSection({ types }: { types: PackageTypeDto[] }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const { canManage } = useWalletPermissions();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<PackageForm>(EMPTY_PACKAGE);
  const [error, setError] = useState('');
  const body = () => ({
    name: form.name.trim(),
    useCount: Number(form.useCount),
    price: form.price.trim(),
    validDays: form.validDays ? Number(form.validDays) : null,
    covers: form.covers,
    buyInAmounts: form.buyInAmounts
      .split(/[,\s]+/)
      .map((a) => a.trim())
      .filter(Boolean),
    allowRebuyAddon: form.allowRebuyAddon,
  });
  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api.post('/members/package-types', body())
        : api.patch(`/members/package-types/${editing}`, body()),
    onSuccess: () => {
      setEditing(null);
      setError('');
      void qc.invalidateQueries({ queryKey: ['package-types'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('createFailed'))),
  });
  const toggle = useMutation({
    mutationFn: (type: PackageTypeDto) =>
      api.patch(`/members/package-types/${type.id}`, { isActive: !type.isActive }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['package-types'] }),
    onError: (failure) => setError(errorMessage(failure, t('createFailed'))),
  });
  const edit = (type: PackageTypeDto) => {
    setEditing(type.id);
    setForm({
      name: type.name,
      useCount: String(type.useCount),
      price: type.price,
      validDays: type.validDays ? String(type.validDays) : '',
      covers: type.covers,
      buyInAmounts: type.buyInAmounts.map((a) => String(Number(a))).join(', '),
      allowRebuyAddon: type.allowRebuyAddon,
    });
  };
  return (
    <section className="space-y-3">
      <SectionTitle title={t('walletPackageTypes')} />
      <TypeTable
        headers={[
          t('walletName'),
          t('walletUseCount'),
          t('walletPrice'),
          t('walletValidDays'),
          t('walletCovers'),
          t('walletBuyIns'),
          t('walletAllowRa'),
          t('walletStatus'),
        ]}
        rows={types.map((type) => ({
          id: type.id,
          active: type.isActive,
          cells: [
            type.name,
            String(type.useCount),
            Number(type.price).toLocaleString(),
            type.validDays ? String(type.validDays) : t('walletNoExpiry'),
            t(`walletCovers_${type.covers}`),
            type.buyInAmounts.length
              ? type.buyInAmounts.map((a) => Number(a).toLocaleString()).join(', ')
              : t('walletAnyBuyIn'),
            type.allowRebuyAddon ? t('walletYes') : t('walletNo'),
            type.isActive ? t('walletActive') : t('walletInactive'),
          ],
          onEdit: canManage ? () => edit(types.find((x) => x.id === type.id)!) : undefined,
          onToggle: canManage ? () => toggle.mutate(type) : undefined,
        }))}
      />
      {canManage && editing === null && (
        <button
          type="button"
          className={SMALL_BUTTON}
          onClick={() => {
            setEditing('new');
            setForm(EMPTY_PACKAGE);
          }}
        >
          {t('walletAddType')}
        </button>
      )}
      {editing !== null && (
        <form
          className={PANEL}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('walletName')}>
              <input
                className={INPUT}
                maxLength={100}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label={t('walletUseCount')}>
              <input
                className={INPUT}
                type="number"
                min="1"
                value={form.useCount}
                onChange={(e) => setForm({ ...form, useCount: e.target.value })}
              />
            </Field>
            <Field label={t('walletPrice')}>
              <input
                className={INPUT}
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </Field>
            <Field label={t('walletValidDays')}>
              <input
                className={INPUT}
                type="number"
                min="1"
                placeholder={t('walletNoExpiry')}
                value={form.validDays}
                onChange={(e) => setForm({ ...form, validDays: e.target.value })}
              />
            </Field>
            <Field label={t('walletCovers')}>
              <select
                className={INPUT}
                value={form.covers}
                onChange={(e) => setForm({ ...form, covers: e.target.value as PackageCoverage })}
              >
                {(['BUY_IN', 'VENUE_FEE', 'BOTH'] as const).map((c) => (
                  <option key={c} value={c}>
                    {t(`walletCovers_${c}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('walletBuyIns')}>
              <input
                className={INPUT}
                placeholder={t('walletAnyBuyIn')}
                value={form.buyInAmounts}
                onChange={(e) => setForm({ ...form, buyInAmounts: e.target.value })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 min-h-11 text-sm">
            <input
              type="checkbox"
              checked={form.allowRebuyAddon}
              onChange={(e) => setForm({ ...form, allowRebuyAddon: e.target.checked })}
            />
            {t('walletAllowRa')}
          </label>
          <FormError message={error} />
          <div className="flex gap-2">
            <button
              type="submit"
              className={BUTTON}
              disabled={
                !form.name.trim() || !(Number(form.useCount) >= 1) || !form.price || save.isPending
              }
            >
              {t('ledgerSave')}
            </button>
            <button type="button" className={SMALL_BUTTON} onClick={() => setEditing(null)}>
              {t('walletCancel')}
            </button>
          </div>
        </form>
      )}
      {editing === null && <FormError message={error} />}
    </section>
  );
}

interface VoucherForm {
  name: string;
  description: string;
  purpose: VoucherPurpose;
  amount: string;
  validDays: string;
  stacksWithOtherCoupons: boolean;
}
const EMPTY_VOUCHER: VoucherForm = {
  name: '',
  description: '',
  purpose: 'AMOUNT_OFF',
  amount: '',
  validDays: '',
  stacksWithOtherCoupons: false,
};

function VoucherTypeSection() {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const { canManage } = useWalletPermissions();
  const { data: types = [] } = useQuery<VoucherTypeDto[]>({
    queryKey: ['voucher-types'],
    queryFn: () => api.get('/members/voucher-types'),
  });
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<VoucherForm>(EMPTY_VOUCHER);
  const [error, setError] = useState('');
  const body = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    purpose: form.purpose,
    amount: form.amount ? form.amount.trim() : null,
    validDays: form.validDays ? Number(form.validDays) : null,
    stacksWithOtherCoupons: form.stacksWithOtherCoupons,
  });
  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api.post('/members/voucher-types', body())
        : api.patch(`/members/voucher-types/${editing}`, body()),
    onSuccess: () => {
      setEditing(null);
      setError('');
      void qc.invalidateQueries({ queryKey: ['voucher-types'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('createFailed'))),
  });
  const toggle = useMutation({
    mutationFn: (type: VoucherTypeDto) =>
      api.patch(`/members/voucher-types/${type.id}`, { isActive: !type.isActive }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['voucher-types'] }),
    onError: (failure) => setError(errorMessage(failure, t('createFailed'))),
  });
  return (
    <section className="space-y-3">
      <SectionTitle title={t('walletVoucherTypes')} shared />
      <TypeTable
        headers={[
          t('walletName'),
          t('walletPurpose'),
          t('walletAmount'),
          t('walletValidDays'),
          t('walletStacks'),
          t('walletStatus'),
        ]}
        rows={types.map((type) => ({
          id: type.id,
          active: type.isActive,
          cells: [
            type.name,
            t(`walletPurpose_${type.purpose}`),
            type.amount ? Number(type.amount).toLocaleString() : '-',
            type.validDays ? String(type.validDays) : t('walletNoExpiry'),
            type.stacksWithOtherCoupons ? t('walletYes') : t('walletNo'),
            type.isActive ? t('walletActive') : t('walletInactive'),
          ],
          onEdit: canManage
            ? () => {
                setEditing(type.id);
                setForm({
                  name: type.name,
                  description: type.description,
                  purpose: type.purpose,
                  amount: type.amount ?? '',
                  validDays: type.validDays ? String(type.validDays) : '',
                  stacksWithOtherCoupons: type.stacksWithOtherCoupons,
                });
              }
            : undefined,
          onToggle: canManage ? () => toggle.mutate(type) : undefined,
        }))}
      />
      {canManage && editing === null && (
        <button
          type="button"
          className={SMALL_BUTTON}
          onClick={() => {
            setEditing('new');
            setForm(EMPTY_VOUCHER);
          }}
        >
          {t('walletAddType')}
        </button>
      )}
      {editing !== null && (
        <form
          className={PANEL}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('walletName')}>
              <input
                className={INPUT}
                maxLength={100}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label={t('walletPurpose')}>
              <select
                className={INPUT}
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value as VoucherPurpose })}
              >
                {(['AMOUNT_OFF', 'VENUE_FEE_WAIVER', 'FREE_ENTRY', 'OTHER'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`walletPurpose_${p}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('walletAmount')}>
              <input
                className={INPUT}
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>
            <Field label={t('walletValidDays')}>
              <input
                className={INPUT}
                type="number"
                min="1"
                placeholder={t('walletNoExpiry')}
                value={form.validDays}
                onChange={(e) => setForm({ ...form, validDays: e.target.value })}
              />
            </Field>
            <Field label={t('walletDescription')}>
              <input
                className={INPUT}
                maxLength={255}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 min-h-11 text-sm">
            <input
              type="checkbox"
              checked={form.stacksWithOtherCoupons}
              onChange={(e) => setForm({ ...form, stacksWithOtherCoupons: e.target.checked })}
            />
            {t('walletStacksHint')}
          </label>
          <FormError message={error} />
          <div className="flex gap-2">
            <button
              type="submit"
              className={BUTTON}
              disabled={
                !form.name.trim() ||
                (form.purpose === 'AMOUNT_OFF' && !form.amount) ||
                save.isPending
              }
            >
              {t('ledgerSave')}
            </button>
            <button type="button" className={SMALL_BUTTON} onClick={() => setEditing(null)}>
              {t('walletCancel')}
            </button>
          </div>
        </form>
      )}
      {editing === null && <FormError message={error} />}
    </section>
  );
}

function MonthlyPlanSection() {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const { canManage } = useWalletPermissions();
  const { data: setting } = useQuery<MonthlyPlanSettingDto>({
    queryKey: ['monthly-plan-setting'],
    queryFn: () => api.get('/members/monthly-plan-setting'),
  });
  const { data: levels = [] } = useQuery<MemberLevelDto[]>({
    queryKey: ['member-levels'],
    queryFn: () => api.get('/members/levels'),
  });
  const [price, setPrice] = useState<string | null>(null);
  const [levelId, setLevelId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const shownPrice = price ?? setting?.price ?? '';
  const shownLevel = levelId ?? setting?.levelId ?? '';
  const save = useMutation({
    mutationFn: () =>
      api.put('/members/monthly-plan-setting', { price: shownPrice.trim(), levelId: shownLevel }),
    onSuccess: () => {
      setError('');
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['monthly-plan-setting'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('createFailed'))),
  });
  return (
    <section className="space-y-3">
      <SectionTitle title={t('walletPlanSetting')} shared />
      <form
        className={PANEL}
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          save.mutate();
        }}
      >
        <p className="text-xs text-base-content/60">{t('walletPlanSettingHint')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('walletPrice')}>
            <input
              className={INPUT}
              type="number"
              min="0"
              step="0.01"
              disabled={!canManage}
              value={shownPrice}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Field>
          <Field label={t('walletPlanLevel')}>
            <select
              className={INPUT}
              disabled={!canManage}
              value={shownLevel}
              onChange={(e) => setLevelId(e.target.value)}
            >
              <option value="">{t('walletPickType')}</option>
              {levels
                .filter((l) => l.isActive)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.displayName}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <FormError message={error} />
        {saved && <p className="text-sm text-success">{t('walletSaved')}</p>}
        {canManage && (
          <button
            type="submit"
            className={BUTTON}
            disabled={!shownPrice || !shownLevel || save.isPending}
          >
            {t('ledgerSave')}
          </button>
        )}
      </form>
    </section>
  );
}

function TypeTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: Array<{
    id: string;
    active: boolean;
    cells: string[];
    onEdit?: () => void;
    onToggle?: () => void;
  }>;
}) {
  const { t } = useTranslation('member');
  return (
    <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-base-300">
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left px-3 py-2 whitespace-nowrap">
                {h}
              </th>
            ))}
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-base-300">
          {rows.map((row) => (
            <tr key={row.id} className={row.active ? '' : 'text-base-content/50'}>
              {row.cells.map((cell, i) => (
                <td key={i} className="px-3 py-2">
                  {cell}
                </td>
              ))}
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  {row.onEdit && (
                    <button type="button" className={SMALL_BUTTON} onClick={row.onEdit}>
                      {t('walletEdit')}
                    </button>
                  )}
                  {row.onToggle && (
                    <button type="button" className={SMALL_BUTTON} onClick={row.onToggle}>
                      {row.active ? t('walletDeactivate') : t('walletActivate')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={headers.length + 1}
                className="px-4 py-6 text-center text-base-content/50"
              >
                {t('ledgerEmpty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
